import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { I18nPipe, ALAIN_I18N_TOKEN } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import { ConstantUtil } from 'src/app/util/constant.util';

import { AiffSettingDetailComponent } from '../detail/aiff-setting-detail.component';

@Component({
  selector: 'aiff-setting-list',
  templateUrl: './aiff-setting-list.component.html',
  styleUrls: ['./aiff-setting-list.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NzFormModule,
    NzButtonModule,
    NzCardModule,
    NzInputModule,
    NzTableModule,
    NzTagModule,
    NzPopconfirmModule,
    NzIconModule,
    NzSpinModule,
    NzModalModule,
    NzDividerModule,
    NzToolTipModule,
    NzEmptyModule,
    NzPaginationModule,
    I18nPipe,
    TestIdDirective
  ]
})
export class AiffSettingListComponent implements OnInit, OnChanges {
  @Input() aiffId: string = '';
  @Input() tenantId: string = '';

  // 列表数据
  listOfData: any = [];
  // 加载状态
  loading = false;
  // 搜索关键词
  refreshTime = Date.now();

  // 添加分页相关属性
  pageIndex = 1;
  pageSize = 10;
  total = 0;

  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  constructor(
    private readonly httpService: HttpService,
    private readonly message: NzMessageService,
    private readonly modal: NzModalService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.aiffId) {
      this.loadData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const aiffIdChange = changes['aiffId'];
    if (
      aiffIdChange &&
      !aiffIdChange.firstChange &&
      aiffIdChange.currentValue &&
      aiffIdChange.currentValue !== aiffIdChange.previousValue
    ) {
      this.pageIndex = 1;
      this.refresh();
    }
  }

  // 加载数据
  loadData(): void {
    this.loading = true;
    const params = {
      aiffId: this.aiffId,
      pageIndex: this.pageIndex - 1,
      pageSize: this.pageSize,
      refreshTime: this.refreshTime,
      direction: 'desc'
    };

    this.httpService
      .post(ConstantUtil.AIFF_SETTING_LIST, params)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.listOfData = res.data.items || [];
          this.total = res.data.totalCount || 0; // 设置总数
        } else {
          this.message.error(this.i18n.fanyi('tenant.aiff.setting.list.load.failed'));
        }
      });
  }

  // 页码变化事件处理
  onPageIndexChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.loadData();
  }

  // 每页条数变化事件处理
  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageIndex = 1; // 重置为第一页
    this.loadData();
  }

  // // 搜索
  // search(): void {
  //   this.pageIndex = 1; // 搜索时重置为第一页
  //   this.loadData();
  // }

  refresh(): void {
    this.pageIndex = 1;
    this.refreshTime = Date.now() + 5000;
    this.loadData();
  }

  // 删除设置
  deleteSetting(id: string): void {
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('tenant.aiff.setting.delete.confirm.title'),
      nzContent: this.i18n.fanyi('tenant.aiff.setting.delete.confirm.content'),
      nzWrapClassName: 'aiff-setting-confirm-modal-shell',
      nzOkDanger: true,
      nzOkText: this.i18n.fanyi('common.confirm'),
      nzCancelText: this.i18n.fanyi('common.cancel'),
      nzOnOk: () => {
        this.httpService.post(`${ConstantUtil.AIFF_SETTING_DELETE}/${id}`, {}).subscribe(res => {
          if (res.success) {
            this.message.success(this.i18n.fanyi('tenant.aiff.setting.delete.success'));
            this.loadData();
          } else {
            showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.aiff.setting.delete.failed'));
          }
        });
      }
    });
  }
  // 获取显示类型的文本
  getDisplayTypeText(type: string): string {
    const typeMap: { [key: string]: string } = {
      Frame: this.i18n.fanyi('tenant.aiff.setting.display.type.frame'),
      AppList: this.i18n.fanyi('tenant.aiff.setting.display.type.applist'),
      Room: this.i18n.fanyi('tenant.aiff.setting.display.type.room'),
      ContactIndex: this.i18n.fanyi('tenant.aiff.setting.display.type.contact-index')
    };
    return typeMap[type] || type;
  }

  // 获取显示类型的颜色
  getDisplayTypeColor(type: string): string {
    const colorMap: { [key: string]: string } = {
      Frame: 'blue',
      AppList: 'green',
      Room: 'purple',
      ContactIndex: 'orange'
    };
    return colorMap[type] ?? 'default';
  }

  getDisplayLocationText(location: string): string {
    const locationMap: Record<string, string> = {
      Toolbar: this.i18n.fanyi('tenant.aiff.setting.display.location.toolbar'),
      Extend: this.i18n.fanyi('tenant.aiff.setting.display.location.extend'),
      Embed: this.i18n.fanyi('tenant.aiff.setting.display.location.embed'),
      MessageMenu: this.i18n.fanyi('tenant.aiff.setting.display.location.message-menu')
    };
    return locationMap[location] || location || '-';
  }

  // 获取弹出视窗大小的文本
  getPopupSizeText(size: string): string {
    const sizeMap: { [key: string]: string } = {
      Full: this.i18n.fanyi('tenant.aiff.setting.popup.size.full'),
      Half: this.i18n.fanyi('tenant.aiff.setting.popup.size.half')
    };
    return sizeMap[size] || size;
  }

  // 获取支援装置的文本
  getSupportDeviceText(device: string): string {
    const deviceMap: { [key: string]: string } = {
      Mobile: this.i18n.fanyi('tenant.aiff.setting.support.device.mobile'),
      Desktop: this.i18n.fanyi('tenant.aiff.setting.support.device.desktop')
    };
    return deviceMap[device] || device;
  }
  addSetting(): void {
    this.modal
      .create({
        nzContent: AiffSettingDetailComponent,
        nzWidth: '1000px',
        nzWrapClassName: 'aiff-setting-detail-modal-shell',
        nzFooter: null,
        nzClosable: false,
        nzData: {
          aiffId: this.aiffId,
          tenantId: this.tenantId,
          isAdd: true
        }
      })
      .afterClose.subscribe(result => {
        if (result) {
          this.refresh();
        }
      });
  }

  // 编辑设置
  editSetting(id: string): void {
    this.modal
      .create({
        nzContent: AiffSettingDetailComponent,
        nzWidth: '1000px',
        nzWrapClassName: 'aiff-setting-detail-modal-shell',
        nzFooter: null,
        nzClosable: false,
        nzData: {
          aiffId: this.aiffId,
          tenantId: this.tenantId,
          settingId: id,
          isAdd: false
        }
      })
      .afterClose.subscribe(result => {
        if (result) {
          this.refresh();
        }
      });
  }
}
